import { useLayoutEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon, ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { Select, SelectItem, Input } from '@heroui/react';
import { useAuthStore } from '../../stores/auth.store';
import menuItems from '../../utils/modulos';
import { createEmpresa } from '@/services/empresa/empresa.service';
import { createEmpresaParametro } from '@/services/empresa/empresa-parametro.service';
import type { EmpresaRequest } from '@/types/empresa.model';
import type { EmpresaParametroRequest } from '@/types/empresa-parametro.model';
import { EMPRESA_PARAMETROS } from '@/utils/constants';
import { linkEmpresaAUsuario } from '@/services/empresa/users.service';
import SubscriptionPlanModal from '@/components/paymentSubscriptions/SubscriptionPlanModal';
import { OpenMenu } from '@/components/shared/OpenMenu';

interface Module {
  id: string;
  name: string;
  slug: string;
  description: string;
  icon: string;
}

export function Onboarding() {
  const { isLoggedIn, /* user */ } = useAuthStore();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  const [companyData, setCompanyData] = useState({
    nombre: '',
    tamano: '1-10' as '1-10' | '11-50' | '51-200' | '200+',
    industria: '',
    email: '',
    telefono: ''
  });

  const [modules, setModules] = useState<Module[]>([]);
  const [selectedModules, setSelectedModules] = useState<string[]>([]);

  // State to store the selected subscription plan
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);

  // State to control the subscription plan modal visibility
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  // State to store all selected data
  const [allSelectedData, setAllSelectedData] = useState<any>(null);

  useLayoutEffect(() => {
    loadModules();
  }, []);

  const loadModules = async () => {
    const data = menuItems.map((item, index) => ({ id: index.toString(), name: item.name, slug: item.href, description: '', icon: '' }));
    setModules(data || []);
  };

  const handleCompanySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (companyData.nombre && companyData.tamano && companyData.industria) {
      // Store company data when moving to step 2
      setAllSelectedData({
        companyData: { ...companyData },
        selectedModules: [ ...selectedModules ]  // Capture current selected modules
      });
      setStep(2);
    }
  };

  // Handle moving from step 2 to step 3
  const handleModuleSelectionSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedModules.length > 0) {
      // Update allSelectedData with modules when moving to step 3
      setAllSelectedData({
        ...allSelectedData,
        selectedModules: [ ...selectedModules ]
      });
      setStep(3);
    }
  };

  // Handle plan selection from modal
  const handlePlanSelection = (planName: string) => {
    setSelectedPlan(planName);
    setShowSubscriptionModal(false);
  };

  // New finalizar method with empresa and empresa-parametro creation
  const handleFinalizar = async () => {
    
    try {
      // Prepare empresa data
      // We'll use the company size as part of the razonSocial or create a separate parameter
      const empresaData: EmpresaRequest = {
        data: {
          nombre: companyData.nombre,
          numeroIdentificacion: '', // Using timestamp as a unique ID for now - this should be a real NIT/ID
          tipoIdentificacion: "NIT", // Assuming NIT as default, adjust as needed
          ...(companyData.email && { email: companyData.email }),
          ...(companyData.telefono && { telefono: companyData.telefono }),
          ...(companyData.industria && { razonSocial: companyData.industria }),
          tamano: companyData.tamano,
          industria: companyData.industria
        }
      };
      
      // Create the empresa
      const empresaResponse = await createEmpresa(empresaData);
      const empresaId = empresaResponse.data.id;
      
      if (empresaId) {
        // Create empresa parametro with selected modules as JSON
        if (selectedModules.length > 0) {
          const parametroData: EmpresaParametroRequest = {
            data: {
              nombre: EMPRESA_PARAMETROS.MODULOS_SELECCIONADOS,
              valor: JSON.stringify(selectedModules), 
              empresa: empresaId,
            }
          };
          
          // Create the empresa parametro
          await createEmpresaParametro(parametroData);
        }

        // Link user to the created empresa
        await linkEmpresaAUsuario(empresaResponse?.data?.documentId ?? "", useAuthStore.getState().user?.id ?? 0);
        if(!empresaResponse.data.documentId )
          throw new Error('No se pudo obtener el ID del documento de la empresa');
        useAuthStore.getState().updateUser({ ...useAuthStore.getState().user!, empresa: empresaResponse.data.documentId });
      }
      
      // Navigate to dashboard after successful creation
      navigate('/dashboard');
    } catch (error: any) {
      // Show error message
      alert(error.message || 'Ocurrió un error al crear la empresa. Por favor intenta nuevamente.');
      console.error('Error creating empresa:', error);
    } finally {
    }
  };

  const toggleModule = (module: Module) => {
    const moduleName = module.name;
    const newSelectedModules = selectedModules.includes(moduleName)
      ? selectedModules.filter(name => name !== moduleName)
      : [...selectedModules, moduleName];
    
    setSelectedModules(newSelectedModules);
    
    // Update allSelectedData with the new module selection if it already exists
    if (allSelectedData) {
      setAllSelectedData({
        ...allSelectedData,
        selectedModules: newSelectedModules
      });
    }
  };

  const handleOpenModal = () => {
    // Placeholder function
  };

  if (!isLoggedIn) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <OpenMenu handleOpenModal={handleOpenModal} handleOpenSignupModal={function (): void { } } />
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="rounded-2xl shadow-lg p-8">
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-3xl font-bold ">Configuración inicial</h1>
              <span className="text-sm font-medium">Paso {step} de 3</span>
            </div>
            <div className="flex gap-2">
              <div className={`flex-1 h-2 rounded-full ${step >= 1 ? 'bg-blue-600' : 'bg-gray-200'}`} />
              <div className={`flex-1 h-2 rounded-full ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`} />
              <div className={`flex-1 h-2 rounded-full ${step >= 3 ? 'bg-blue-600' : 'bg-gray-200'}`} />
            </div>
          </div>

          {step === 1 && (
            <form onSubmit={handleCompanySubmit} className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-4">
                  Información de tu empresa
                </h2>
                <p className="mb-6">
                  Ayúdanos a personalizar tu experiencia proporcionando algunos datos básicos.
                </p>
              </div>

              <div>
                <label htmlFor="company-name" className="block text-sm font-medium mb-2">
                  Nombre de la empresa <span className="text-red-500">*</span>
                </label>
                <Input
                  id="company-name"
                  required
                  value={companyData.nombre}
                  onChange={(e) => setCompanyData({ ...companyData, nombre: e.target.value })}
                  placeholder="Ej: Mi Empresa S.A.S."
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="company-size" className="block text-sm font-medium mb-2">
                  Tamaño de la empresa <span className="text-red-500">*</span>
                </label>
                <Select
                  id="company-size"
                  required
                  selectedKeys={[companyData.tamano]}
                  onSelectionChange={(keys) => setCompanyData({ ...companyData, tamano: Array.from(keys)[0] as any })}
                  className="w-full"
                >
                  <SelectItem key="1-10">1-10 empleados</SelectItem>
                  <SelectItem key="11-50">11-50 empleados</SelectItem>
                  <SelectItem key="51-200">51-200 empleados</SelectItem>
                  <SelectItem key="200+">Más de 200 empleados</SelectItem>
                </Select>
              </div>

              <div>
                <label htmlFor="industry" className="block text-sm font-medium mb-2">
                  Industria o sector <span className="text-red-500">*</span>
                </label>
                <Select
                  id="industry"
                  required
                  selectedKeys={companyData.industria ? [companyData.industria] : []}
                  onSelectionChange={(keys) => setCompanyData({ ...companyData, industria: Array.from(keys)[0] as string })}
                  className="w-full"
                >
                  <SelectItem key="">Selecciona una industria</SelectItem>
                  <SelectItem key="Agricultura, ganadería, silvicultura y pesca">Agricultura, ganadería, silvicultura y pesca</SelectItem>
                  <SelectItem key="Minería y extracción">Minería y extracción</SelectItem>
                  <SelectItem key="Manufactura e industria pesada">Manufactura e industria pesada</SelectItem>
                  <SelectItem key="Energía y servicios públicos">Energía y servicios públicos</SelectItem>
                  <SelectItem key="Construcción e infraestructura">Construcción e infraestructura</SelectItem>
                  <SelectItem key="Transporte y logística">Transporte y logística</SelectItem>
                  <SelectItem key="Comercio mayorista y minorista">Comercio mayorista y minorista</SelectItem>
                  <SelectItem key="Tecnología de la información y comunicaciones (TIC)">Tecnología de la información y comunicaciones (TIC)</SelectItem>
                  <SelectItem key="Servicios financieros y seguros">Servicios financieros y seguros</SelectItem>
                  <SelectItem key="Bienes raíces y propiedad">Bienes raíces y propiedad</SelectItem>
                  <SelectItem key="Salud y servicios sociales">Salud y servicios sociales</SelectItem>
                  <SelectItem key="Educación y formación profesional">Educación y formación profesional</SelectItem>
                  <SelectItem key="Turismo, hotelería y gastronomía">Turismo, hotelería y gastronomía</SelectItem>
                  <SelectItem key="Medios, arte y entretenimiento">Medios, arte y entretenimiento</SelectItem>
                  <SelectItem key="Servicios profesionales, científicos y técnicos">Servicios profesionales, científicos y técnicos</SelectItem>
                  <SelectItem key="Administración pública y defensa">Administración pública y defensa</SelectItem>
                  <SelectItem key="Organizaciones sin fines de lucro y ONG">Organizaciones sin fines de lucro y ONG</SelectItem>
                  <SelectItem key="Servicios personales y domésticos">Servicios personales y domésticos</SelectItem>
                  <SelectItem key="Sector ambiental y sostenibilidad">Sector ambiental y sostenibilidad</SelectItem>
                  <SelectItem key="Investigación y desarrollo (I+D)">Investigación y desarrollo (I+D)</SelectItem>
                </Select>
              </div>

              <div>
                <label htmlFor="company-email" className="block text-sm font-medium mb-2">
                  Correo electrónico de la empresa <span className="text-gray-400 text-sm">(opcional)</span>
                </label>
                <Input
                  type="email"
                  id="company-email"
                  value={companyData.email}
                  onChange={(e) => setCompanyData({ ...companyData, email: e.target.value })}
                  placeholder="Ej: contacto@miempresa.com"
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="company-phone" className="block text-sm font-medium mb-2">
                  Teléfono de la empresa <span className="text-gray-400 text-sm">(opcional)</span>
                </label>
                <Input
                  type="tel"
                  id="company-phone"
                  value={companyData.telefono}
                  onChange={(e) => setCompanyData({ ...companyData, telefono: e.target.value })}
                  placeholder="Ej: +57 300 123 4567"
                  className="w-full"
                />
              </div>

              <div className="flex justify-end pt-4">
                <button
                  type="submit"
                  className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  Siguiente
                  <ArrowRightIcon className="w-5 h-5" />
                </button>
              </div>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleModuleSelectionSubmit} className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold  mb-4">
                  ¿Qué deseas gestionar primero?
                </h2>
                <p className="text-gray-600 mb-6">
                  Selecciona los módulos que necesitas. Podrás activar más módulos más adelante.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {modules.map((module) => {
                  const isSelected = selectedModules.includes(module.name);
                  return (
                    <button
                      key={module.id}
                      type="button"
                      onClick={() => toggleModule(module)}
                      className={`p-4 rounded-lg border-2 transition-all text-left relative ${
                        isSelected
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      {isSelected && (
                        <div className="absolute top-3 right-3">
                          <CheckCircleIcon className="w-5 h-5 text-blue-600" />
                        </div>
                      )}
                      <h3 className="font-semibold text-gray-900 mb-1 pr-8">{module.name}</h3>
                      <p className="text-sm text-gray-600">{module.description}</p>
                    </button>
                  );
                })}
              </div>

              <div className="flex justify-between pt-4">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="px-6 py-3  font-semibold rounded-lg border border-gray-300 hover:bg-gray-500 transition-colors flex items-center gap-2"
                >
                  <ArrowLeftIcon className="w-5 h-5" />
                  Anterior
                </button>
                <button
                  type="submit"
                  disabled={selectedModules.length === 0}
                  className="px-6 py-3 bg-blue-600  font-semibold rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Siguiente
                  <ArrowRightIcon className="w-5 h-5" />
                </button>
              </div>
            </form>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold  mb-4">
                  Selecciona tu plan de suscripción
                </h2>
                <p className="text-gray-600 mb-6">
                  Elige el plan que mejor se adapte a las necesidades de tu empresa.
                </p>
              </div>

              <div className="flex flex-col items-center">
                <button
                  onClick={() => setShowSubscriptionModal(true)}
                  className="px-6 py-3 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-colors"
                >
                  {selectedPlan ? `Plan seleccionado: ${selectedPlan}` : 'Seleccionar plan de suscripción'}
                </button>
              </div>

              <div className="flex justify-between pt-4">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="px-6 py-3  font-semibold rounded-lg border border-gray-300 hover:bg-gray-500 transition-colors flex items-center gap-2"
                >
                  <ArrowLeftIcon className="w-5 h-5" />
                  Anterior
                </button>
                <button
                  type="button"
                  disabled={!selectedPlan}
                  onClick={handleFinalizar}
                  className="px-6 py-3 bg-blue-600  font-semibold rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Finalizar
                </button>
              </div>
            </div>
          )}

          {/* Subscription Plan Modal */}
          <SubscriptionPlanModal 
            isOpen={showSubscriptionModal} 
            onClose={() => setShowSubscriptionModal(false)}
            onPlanSelect={handlePlanSelection}
          />
        </div>
      </div>
    </div>
  );
}
